import config from '@config';
import { loadCssFile, loadJavaScriptFile } from '@xFrame4/common/Functions';

export class LazyLoadingHelper
{
    loadingCssFontsStarted = false;
    loadingJavascriptFilesStarted = false;

    setup()
    {
        this.lazyLoadImages();
        this.lazyLoadIframes();
    }

    /**
     * Lazy load the images.
     * 
     * @param verticalLazyLoadThreshold The threshold in pixels from where to start loading the images.
     * @param horizontalLazyLoadThreshold The threshold in pixels from where to start loading the images.
     */
    lazyLoadImages(verticalLazyLoadThreshold: number = 200, horizontalLazyLoadThreshold: number = 50): void
    {
        let options: IntersectionObserverInit = {
            root: null,
            rootMargin: `${horizontalLazyLoadThreshold}px ${verticalLazyLoadThreshold}px`, // horizontal and vertical margins
            threshold: 0,
        };

        const loadImage = (image: HTMLImageElement): void =>
        {
            const src = image.getAttribute('data-src');
            if (src)
            {
                image.src = src;
                image.removeAttribute('data-src');
            }
        };

        const onIntersection: IntersectionObserverCallback = (entries, observer) =>
        {
            entries.forEach((entry) =>
            {
                if (entry.isIntersecting)
                {
                    const image = entry.target as HTMLImageElement;
                    loadImage(image);
                    observer.unobserve(image);
                }
            });
        };

        const observeImages = (images: NodeListOf<Element>, observer: IntersectionObserver): void =>
        {
            images.forEach((image) =>
            {
                observer.observe(image);
            });
        };

        let observer = new IntersectionObserver(onIntersection, options);
        let images = document.querySelectorAll('img[smooth-image]');

        observeImages(images, observer);

        // Create a MutationObserver to watch for new images added to the DOM.
        const mutationObserver = new MutationObserver((mutations) =>
        {
            mutations.forEach((mutation) =>
            {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0)
                {
                    mutation.addedNodes.forEach((node) =>
                    {
                        if (node.nodeType === Node.ELEMENT_NODE)
                        {
                            let element = node as HTMLElement;
                            let newImages = element.querySelectorAll('img[smooth-image]');

                            if (newImages.length > 0)
                            {
                                observeImages(newImages, observer);
                            }
                        }
                    });
                }
            });
        });

        let observerConfig: MutationObserverInit = {
            childList: true,
            subtree: true,
        };

        mutationObserver.observe(document.body, observerConfig);
    }

    /**
     * Lazy load iframes.
     * 
     * @param lazyLoadThreshold The threshold in pixels.
     */
    lazyLoadIframes(lazyLoadThreshold: number = 200): void
    {
        let options: IntersectionObserverInit = {
            root: null,
            rootMargin: `${lazyLoadThreshold}px`,
            threshold: 0,
        };

        const loadIframe = (iframe: HTMLIFrameElement): void =>
        {
            const src = iframe.getAttribute('data-src');
            if (src)
            {
                iframe.src = src;
                iframe.removeAttribute('data-src');
            }
        };

        const onIntersection: IntersectionObserverCallback = (entries, observer) =>
        {
            entries.forEach((entry) =>
            {
                if (entry.isIntersecting)
                {
                    const iframe = entry.target as HTMLIFrameElement;
                    loadIframe(iframe);
                    observer.unobserve(iframe);
                }
            });
        };

        const observeIframes = (iframes: NodeListOf<Element>, observer: IntersectionObserver): void =>
        {
            iframes.forEach((iframe) =>
            {
                observer.observe(iframe);
            });
        };

        let observer = new IntersectionObserver(onIntersection, options);
        let iframes = document.querySelectorAll('iframe[smooth-iframe]');

        observeIframes(iframes, observer);

        // Create a MutationObserver to watch for new iframes added to the DOM.
        const mutationObserver = new MutationObserver((mutations) =>
        {
            mutations.forEach((mutation) =>
            {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0)
                {
                    mutation.addedNodes.forEach((node) =>
                    {
                        if (node.nodeType === Node.ELEMENT_NODE)
                        {
                            const element = node as HTMLElement;
                            const newIframes = element.querySelectorAll('iframe[smooth-iframe]');

                            if (newIframes.length > 0)
                            {
                                observeIframes(newIframes, observer);
                            }
                        }
                    });
                }
            });
        });

        const observerConfig: MutationObserverInit = {
            childList: true,
            subtree: true,
        };

        mutationObserver.observe(document.body, observerConfig);
    }
}

export default LazyLoadingHelper;